/* import __COLOCATED_TEMPLATE__ from './glossary.hbs'; */
/* RESPONSIBLE TEAM: team-help-desk-experience */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type GlossaryService from 'embercom/services/glossary-service';
import type Session from 'embercom/services/session';
import type IntlService from 'embercom/services/intl';
interface TableRow {
  id: number;
  translations?: Translation;
  non_translatable?: boolean;
  [key: string]: string | number | boolean | Translation | undefined;
}

interface Translation {
  [locale: string]: string;
}

interface GlossaryItem {
  translations: Translation;
  non_translatable: boolean;
}
export default class Glossary extends Component<{
  openSideDrawer: (rowData: any, mode: 'new' | 'edit') => void;
}> {
  @service declare glossaryService: GlossaryService;
  @service declare session: Session;
  @service declare intl: IntlService;
  @tracked columns: Array<{ label: string; valuePath: string; width?: string }> = [];
  @tracked selectedTab = 'glossary';
  @tracked isLoadingMore = false;
  @tracked isSideDrawerOpen = false;
  @tracked rowData: GlossaryItem | undefined;
  @tracked hoveredRowId: number | null = null;

  constructor(
    owner: unknown,
    args: { openSideDrawer: (rowData: any, mode: 'new' | 'edit') => void },
  ) {
    super(owner, args);
    this.loadGlossary();
  }

  @action
  async loadGlossary() {
    await this.glossaryService.loadGlossary();
    this.buildColumns();
  }

  @action
  setHoveredRow(rowId: number | null) {
    this.hoveredRowId = rowId;
  }

  @action
  async loadMoreItems() {
    this.isLoadingMore = true;
    try {
      this.glossaryService.loadMoreItems();
    } finally {
      this.isLoadingMore = false;
    }
  }

  getLanguageFromCode(languageCode: string, locale: string): string {
    try {
      let baseLanguageCode = languageCode.split('-')[0];

      let languageNames = new Intl.DisplayNames([locale], { type: 'language' });

      return languageNames.of(languageCode) || languageNames.of(baseLanguageCode) || languageCode;
    } catch (e) {
      return languageCode;
    }
  }

  getLanguageDisplayName(langCode: string): string {
    return this.getLanguageFromCode(langCode, this.session.locale);
  }

  buildColumns() {
    let languages = Array.from(this.glossaryService.availableLanguages).sort();

    if (languages.includes(this.appLocale)) {
      let appLocaleIndex = languages.indexOf(this.appLocale);
      languages.splice(appLocaleIndex, 1);
      languages.unshift(this.appLocale);
    }

    this.columns = languages.map((langCode) => ({
      label:
        langCode === this.appLocale
          ? this.intl.t('new-settings.workspace.multilingual.glossary.original-language')
          : this.getLanguageDisplayName(langCode),
      valuePath: langCode,
      width: `${Math.floor(100 / languages.length)}%`,
    }));
  }

  get appLocale() {
    return this.session.locale.split('-')[0];
  }

  get isLoading() {
    return this.glossaryService.isLoading;
  }

  get glossaryCount() {
    return this.glossaryService.totalItems;
  }

  get displayCount() {
    return this.glossaryService.displayGlossary.length;
  }

  get canLoadMore() {
    return this.glossaryService.canLoadMore;
  }

  get glossaryItems() {
    // Transform the data structure for the table
    return this.glossaryService.displayGlossary.map((item) => {
      let row: TableRow = {
        id: item.id,
      };

      // Create translations object in the expected format
      let translations: Translation = {};
      if (item.translations) {
        Object.entries(item.translations).forEach(([langCode, text]) => {
          translations[langCode] = text;
          row[langCode] = text; // Keep the flat structure for table display
        });
      }

      // Add the properly structured data for the side drawer
      row['translations'] = translations;
      row['non_translatable'] = item.non_translatable || false;

      return row;
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'NewSettings::Workspace::Multilingual::Glossary': typeof Glossary;
  }
}
